import { Component } from '@theme/component';
import { fetchConfig, updateCartCount } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import * as haptic from '@theme/haptic';

/** @typedef {import('./types.js').ProductVariant} ProductVariant */

/**
 * @typedef {Object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {HTMLElement} liveRegion
 *
 * @extends {Component<ProductFormRefs>}
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Reset abort controller in case of reconnect after morph
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    haptic.haptic();

    const button = this.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const formData = new FormData(form);

    const sectionIds = Array.from(document.querySelectorAll('[data-cart-section]'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const errorMessage = response.description || response.message || Theme.translations.cart_error;
          this.#setLiveRegion(errorMessage);
          this.#showVisibleError(errorMessage);
          haptic.error();
          this.dispatchEvent(new CartErrorEvent(this.id || '', response.message, response.description, response.errors));
          return;
        }

        return fetch(`${Theme.routes.cart_url}.js`)
          .then((cartResponse) => cartResponse.json())
          .catch(() => null)
          .then((cart) => {
            const cartItemCount = typeof cart?.item_count === 'number' ? cart.item_count : undefined;
            const fallbackItemCount = Number(formData.get('quantity')) || 1;
            const itemCount = cartItemCount ?? fallbackItemCount;

            this.#setLiveRegion(Theme.translations.added);
            haptic.confirm();

            // Updated here rather than left to a listener: `cart-items-component` only exists on the
            // cart page, so on a PDP or a listing nothing else would refresh the header badge.
            // Only the real cart total is written — the fallback above is the added quantity, which
            // would show a plainly wrong number if `/cart.js` failed.
            updateCartCount(cartItemCount);

            this.dispatchEvent(
              new CartAddEvent(response, this.id || '', {
                source: 'product-form-component',
                productId: this.dataset.productId,
                itemCount,
                // The whole cart, not just its count: listeners that need the new state (Nosto cart
                // tagging) would otherwise re-fetch what we already have in hand.
                cart,
                sections: response.sections,
              })
            );
          });
      })
      .catch((error) => {
        console.error(error);
        this.#setLiveRegion(Theme.translations.cart_error);
      })
      .finally(() => {
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    const variant = /** @type {ProductVariant | null} */ (event.detail?.resource);

    if (event.detail?.data?.productId && this.dataset.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    this.refs.variantId.value = variant?.id ? String(variant.id) : '';

    const button = this.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = !variant?.available;
    }
  };

  /**
   * @param {string | undefined} text
   */
  #setLiveRegion(text) {
    this.refs.liveRegion.textContent = text || '';

    window.setTimeout(() => {
      this.refs.liveRegion.textContent = '';
    }, 5000);
  }

  /**
   * @param {string} message
   */
  #showVisibleError(message) {
    // Remove any existing error
    this.querySelector('.product-form__error')?.remove();

    const error = document.createElement('p');
    error.className = 'product-form__error';
    error.setAttribute('role', 'alert');
    error.textContent = message;

    const button = this.querySelector('[ref="addToCartButton"]');
    if (button) {
      button.insertAdjacentElement('beforebegin', error);
    } else {
      this.appendChild(error);
    }

    window.setTimeout(() => {
      error.remove();
    }, 5000);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
